import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, FormGroup } from "react-bootstrap";
import { Redirect } from "react-router-dom";
import { createStructuredSelector } from "reselect";
import {
    getAppFeatures,
    getAppIsCreatePasswordLoading,
    getIsFirstLoginSelector,
    getIsCheckCodeLoading,
    makeSelectAppFormError,
    makeSelectAppLoggedIn,
    makeSelectChangeEmail,
    getCodeChecked,
    getPasswordMinLengthSelector,
} from "../../../app/appSelectors.js";
import { connect } from "react-redux";
import { LoginBoxStyled, LoginStyled } from "./styled/LoginStyled.js";
import LoginWrapper from "./LoginWrapper.jsx";
import {
    checkCodeAction,
    createPasswordAction,
    registrationAction,
    resendCodeAction,
    setChangeEmailSuccess
} from "../loginActions.js";
import { useLocation } from "react-router";
import { isClientMobile } from "../../../components/ClientInfo/ClientInfo";
import FormPasswordEye from "../../../components/FormPasswordEye/FormPasswordEye.jsx";
import { openModalAction, resetFormErrorAction } from "../../../app/appActions";
import { Loader } from "../../../components/Loader/Loader";
import { RouterPaths, URL_SEARCH_PARAMS } from "../../../app/appConstants";
import PasswordTooltip from "../../../components/PasswordTooltip/PasswordTooltip";
import { usePasswordTooltip } from "../../../components/PasswordTooltip/passwordTooltipHooks";
import { ConfirmLinkWrapperStyled } from "./styled/ConfirmStyled";
import ResendCodeButton from "./ResendCodeButton";
import appHistory from "../../../app/appHistory";
import _ from "lodash";
import RegistrationAcceptPolicy from "./RegistrationAcceptPolicy";
import { useInitialUrlSearchParams } from "../../../hooks/useInitialUrlSearchParams";
import { MODALS_NAME } from "../../Modals/modalsConstants";
import { useLangContext } from "../../../providers/LangProvider/LangProviderContext.js";
import { isEmailValid } from "../../../utils/escapeUtils";

const CODE_DIGITS_COUNT = 4;

function Confirm(props) {
    const {getLangPath} = useLangContext();
    const {
        features,
        formError,
        loggedIn,
        registration,
        checkCode,

        changeEmail,
        setChangeEmail,
        codeChecked,
        createPassword,
        isCheckCodeLoading,
        isCreatePasswordLoading,
        isFirstLogin,
        passwordMinLength,
        resetFormErrorAction,
    } = props;
    const location = useLocation();

    const {acceptPolicy} = useInitialUrlSearchParams([URL_SEARCH_PARAMS.ACCEPT_POLICY], false);

    const {
        isShowPasswordTooltip, checkPasswordValue, onBlurPasswordField, onSelectPasswordField, onChangePasswordField,
    } = usePasswordTooltip();

    const [codeDigits, setCodeDigits] = useState(_.fill(Array(CODE_DIGITS_COUNT), ''));

    const {email, code, registerEmail, justConfirm} = useMemo(() => {
        const params = new URLSearchParams(location.search);
        const email = params.get('email');
        const code = params.get('code');
        const justConfirm = params.get('just-confirm');
        const registerEmail = params.get('register-email');
        return {email, code, justConfirm, registerEmail: isEmailValid(registerEmail) ? registerEmail : ''}
    }, [location]);

    useEffect(() => {
        if (!email && !registerEmail) {
            appHistory.push(RouterPaths.login);
            return;
        }
        if (!registerEmail && !codeChecked) {
            checkCode({
                email,
                code,
                failureRedirectLink: RouterPaths.brokenLink,
                confirmEmail: justConfirm,
            });
        }
    }, [location.search, checkCode, email, registerEmail, code, codeChecked, justConfirm]);


    const submitChangeEmailHandler = useCallback((e) => {
        e.preventDefault();
        registration({
            email: e.target.querySelector('[name=email]').value,
        });
    }, [registration]);

    const submitSavePasswordHandler = useCallback((e) => {
        e.preventDefault();
        const password = e.target.querySelector('[name=password]').value;
        const password2 = e.target.querySelector('[name=password]').value;

        const params = new URLSearchParams(location.search);
        const email = params.get('email');
        const code = params.get('code');
        createPassword({
            email,
            code,
            password,
            password2,
        });
    }, [createPassword, location.search]);

    const handleCheckCode = useCallback((code) => {
        checkCode({
            email: registerEmail,
            code,
            onSuccess: () => appHistory.push(`${RouterPaths.confirm}?email=${registerEmail}&code=${code}`)
        });
    }, [checkCode, registerEmail])

    const submitCheckCodeHandler = useCallback((e) => {
        e.preventDefault();
        const currentCode = codeDigits.join('');
        handleCheckCode(currentCode);
    }, [codeDigits, handleCheckCode]);

    const handleChangeEmail = useCallback(() => {
        if (formError) {
            resetFormErrorAction()
        }
        setChangeEmail(true);
    }, [formError, resetFormErrorAction, setChangeEmail])

    if (loggedIn && !justConfirm) {
        if (isClientMobile && isFirstLogin) {
            return <Redirect to={RouterPaths.mobileDepositBonus}/>
        } else {
            return <Redirect to={RouterPaths.trade}/>
        }
    }

    return <LoginWrapper hideAuthButton>
        <LoginStyled>
            {
                    !justConfirm && (
                            <LoginBoxStyled>
                                {
                                        !!code && <>
                                            <FormGroup className="mb-4">
                                                <h1>{getLangPath('email_confirmation.create_password')}</h1>
                                            </FormGroup>
                                            <p className={'mb-4'}
                                               dangerouslySetInnerHTML={{__html: getLangPath('email_confirmation.welcome_text')}}/>
                                            <Form onSubmit={submitSavePasswordHandler}>
                                                <PasswordTooltip password={checkPasswordValue} show={isShowPasswordTooltip}>
                                                    <Form.Group className="mb-4">
                                                        <FormPasswordEye
                                                                name="password"
                                                                placeholder={getLangPath('promo.fx.new_password.title')}
                                                                isInvalid={formError}
                                                                required
                                                                onSelect={onSelectPasswordField}
                                                                onBlur={onBlurPasswordField}
                                                                onChange={onChangePasswordField}
                                                        />
                                                        {
                                                                formError && <>
                                                                    <Form.Control.Feedback type="invalid">
                                                                        {getLangPath(formError, {count: passwordMinLength})}
                                                                    </Form.Control.Feedback>
                                                                </>
                                                        }
                                                    </Form.Group>
                                                </PasswordTooltip>

                                                {!!acceptPolicy && <RegistrationAcceptPolicy className="mb-4"/>}

                                                <div className="d-grid">
                                                    <Button size={'lg'} variant={'primary'} type={'submit'}>
                                                        {getLangPath('email_confirmation.save_password')}
                                                    </Button>
                                                </div>
                                            </Form>
                                        </>
                                }
                                {
                                        !code && <>
                                            {
                                                    !changeEmail && <>
                                                        <h1 className="m-0"
                                                            dangerouslySetInnerHTML={{__html: getLangPath('email_confirmation.confirm_email')}}/>
                                                        <p className="mt-2rem"
                                                           dangerouslySetInnerHTML={{__html: getLangPath('email_confirmation.send_link_to_email', {email: registerEmail})}}/>
                                                        <p className="mt-2rem">{getLangPath('email_confirmation.check_spam')}</p>
                                                        <div className="d-flex flex-column mt-2rem">
                                                            <ResendCodeButton
                                                                    email={registerEmail}
                                                                    resendLabel="email_confirmation.request_link"
                                                                    resendOnTimerLabel="email_confirmation.resend_link"
                                                                    resendSuccessLabel="email_confirmation.send_link"
                                                            />
                                                            <ConfirmLinkWrapperStyled className="d-flex flex-column mt-2rem">
                                                                <Button variant={'link'} onClick={handleChangeEmail}>
                                                                    {getLangPath('email_confirmation.change_email')}
                                                                </Button>
                                                            </ConfirmLinkWrapperStyled>
                                                        </div>
                                                    </>
                                            }
                                            {
                                                    !!changeEmail && <>
                                                        <FormGroup className="mb-4">
                                                            <h1>{getLangPath('email_confirmation.change_email')}</h1>
                                                        </FormGroup>
                                                        <p className={'mb-4'}
                                                           dangerouslySetInnerHTML={{__html: getLangPath('email_confirmation.mistake_email', {email: registerEmail})}}/>
                                                        <Form onSubmit={submitChangeEmailHandler}>
                                                            <Form.Group className="mb-4">
                                                                <Form.Control name="email" type="email"
                                                                              placeholder={getLangPath('email_confirmation.new_email')}
                                                                              isInvalid={formError} required/>
                                                                {
                                                                        formError && <>
                                                                            <Form.Control.Feedback type="invalid">
                                                                                {getLangPath(formError)}
                                                                            </Form.Control.Feedback>
                                                                        </>
                                                                }
                                                            </Form.Group>

                                                            <div className="d-flex flex-column align-items-center">
                                                                <Button size={'lg'} variant={'info'} type={'submit'} className={'mb-4 w-100'}>
                                                                    {getLangPath('email_confirmation.save_email')}
                                                                </Button>
                                                                <ConfirmLinkWrapperStyled>
                                                                    <Button size={'md'} variant={'link'} onClick={() => {
                                                                        setChangeEmail(false);
                                                                    }}>
                                                                        {getLangPath('email_confirmation.back_to_confirmation')}
                                                                    </Button>
                                                                </ConfirmLinkWrapperStyled>
                                                            </div>
                                                        </Form>
                                                    </>
                                            }
                                        </>
                                }
                            </LoginBoxStyled>
                    )
            }
            {!!justConfirm && codeChecked && <Redirect to={{
                pathname: RouterPaths.trade,
                search: `?m=${MODALS_NAME.EMAIL_CONFIRMED}`,
                state: {checkEmail: email},
            }}/>}
            <Loader show={isCheckCodeLoading || isCreatePasswordLoading}/>
        </LoginStyled>
    </LoginWrapper>
}

const mapStateToProps = createStructuredSelector({
    features: getAppFeatures,
    formError: makeSelectAppFormError(),
    changeEmail: makeSelectChangeEmail(),
    codeChecked: getCodeChecked,
    isCheckCodeLoading: getIsCheckCodeLoading,
    isCreatePasswordLoading: getAppIsCreatePasswordLoading,
    isFirstLogin: getIsFirstLoginSelector,
    loggedIn: makeSelectAppLoggedIn(),
    passwordMinLength: getPasswordMinLengthSelector,
});

const mapDispatchToProps = (dispatch) => ({
    checkCode: (payload) => dispatch(checkCodeAction(payload)),
    openModal: (payload) => dispatch(openModalAction(payload)),
    registration: (payload) => dispatch(registrationAction(payload)),
    setChangeEmail: (payload) => dispatch(setChangeEmailSuccess(payload)),
    createPassword: (payload) => dispatch(createPasswordAction(payload)),
    resendCode: (payload) => dispatch(resendCodeAction(payload)),
    resetFormErrorAction: (payload) => dispatch(resetFormErrorAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Confirm);
